import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { MultiGroupSelectButtonType } from 'types/ButtonTypes';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import '../../style/scss/button.scss';
import '../../style/scss/tooltip.scss';

const MultiGroupSelectButton: React.FC<MultiGroupSelectButtonType> = ({
    groups, 
    label, 
    size = 'medium', 
    loading = false, 
    type = 'button', 
    selected,
    onChange,
    onlyViewRestriction,
    customClass
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const toggleDropdown = () => setIsOpen(!isOpen);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

    // Make the button invisible if it is restricted for only_view users
    const userHasOnlyViewRight = !useAllowedRight('only_view')
    const isVisible = onlyViewRestriction ? userHasOnlyViewRight : true;

    // Set the size, button and text loading classes
    const buttonSizeClass = `button-${size}`;
    const buttonLoadingClass = loading ? 'button-loading' : '';
    const textLoadingClass = loading ? 'text-hidden' : '';

    // Give the loader icon a delay, so it looks smoother
    const [showLoaderWithDelay, setShowLoaderWithDelay] = useState(false);
    useEffect(() => {
        let timer: number;
        if (loading) {
            timer = window.setTimeout(() => setShowLoaderWithDelay(true), 150)
        } else {
            setShowLoaderWithDelay(false);
        }
        return () => clearTimeout(timer);
    }, [loading]);

    // Close the dropdown when clicking outside it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Keep the opened menu within the viewport by limiting its height and alignment
    useLayoutEffect(() => {
        if (!isOpen || !menuRef.current || !dropdownRef.current) {
            setMenuStyle({});
            return;
        }

        // Measure the button and the freshly rendered menu
        const viewportMargin = 16;
        const buttonRect = dropdownRef.current.getBoundingClientRect();
        const menuRect = menuRef.current.getBoundingClientRect();

        // Limit the menu height to the space below the button
        const availableHeight = window.innerHeight - buttonRect.bottom - viewportMargin;
        const style: React.CSSProperties = { maxHeight: Math.max(availableHeight, 160), overflowY: 'auto' };

        // Align the menu to the right when it runs out of the screen
        if (menuRect.right > window.innerWidth - viewportMargin) {
            style.left = 'auto';
            style.right = 0;
            style.marginLeft = 0;
        }

        setMenuStyle(style);
    }, [isOpen]);

    // Handle selection of an item
    const handleSelect = (groupId: string, value: string | number) => {
        const isSelected = selected[groupId]?.includes(value);

        const updated = {
            ...selected,
            [groupId]: isSelected
                ? selected[groupId].filter(v => v !== value)
                : [...(selected[groupId] ?? []), value]
        };

        onChange(updated);
    };

    // Get the labels of the selected items
    const selectedLabels = groups.flatMap(group => group.options
        .filter(option => selected[group.id]?.includes(option.value))
        .map(option => option.label)
    );

    return (
        isVisible ? (
            <div className='dropdown-button' ref={dropdownRef}>
                <button type={type}
                        onClick={toggleDropdown}
                        className={`button ${buttonSizeClass} button-secondary ${customClass} ${isOpen ? 'button-active' : ''} ${buttonLoadingClass}`}
                        style={{ position: 'relative' }}
                        disabled={loading}>
                    <span className={`button-text ${textLoadingClass}`}>
                        {size === 'extra-small' ? (
                            selectedLabels.length === 0
                                ? t(label)
                                : `${t('Filters')} (${selectedLabels.length})`
                        ) : (
                            selectedLabels.length === 0
                                ? t(label)
                                : selectedLabels.join(', ')
                        )}
                    </span>
                    {showLoaderWithDelay && 
                        <div className='loader-container'>
                            <div className='button-loader' />
                        </div>
                    }
                    <FontAwesomeIcon
                        icon={!isOpen ? faCaretDown : faCaretUp}
                        style={{ marginLeft: '0.3rem', paddingBottom: '0.025rem' }}
                    />
                </button>
                {isOpen && (
                    <div className='dropdown-button-menu multi-group-menu'
                         ref={menuRef}
                         style={menuStyle}
                         key={JSON.stringify(groups.map(g => g.options.map(o => o.value)))}>
                        {groups.map(group => (
                            <div key={group.id} 
                                 className={`dropdown-group ${group.optionType ? 'badge-group' : ''}`}>
                                <div className="dropdown-group-label">
                                    {t(group.label)}
                                </div>
                                {group.options.map(option => {
                                    const isSelected = selected[group.id]?.includes(option.value);
                                    return (
                                        <div key={option.value}
                                             className='dropdown-button-item'
                                             onClick={() => handleSelect(group.id, option.value)}>

                                            {(group.optionType === 'label-badge' || group.optionType === 'status-badge' ) ? (
                                                <span className={`${group.optionType} ${option.color}`}>
                                                    {t(option.label)}
                                                </span>
                                            ) : (
                                                t(option.label)
                                            )}

                                            {isSelected && (
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className="filter-dropdown-icon"
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        ) : null
    );
};

export default MultiGroupSelectButton;